import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, useMotionValue, useTransform, animate, AnimatePresence, useMotionValueEvent } from 'framer-motion';
import {
    X, Check, ZoomIn, ZoomOut, RotateCw, FlipHorizontal,
    Grid3X3, Crosshair, RefreshCw, MousePointer2,
    Maximize2, Square, Layers
} from 'lucide-react';
import toast from 'react-hot-toast';

interface OneToOneCropperProps {
    imageUrl: string;
    onCrop: (croppedFile: File) => void;
    onCancel: () => void;
}

// Debounce helper for performance
const debounce = <T extends (...args: any[]) => void>(func: T, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};

// Throttle helper for performance
const throttle = <T extends (...args: any[]) => void>(func: T, limit: number) => {
    let inThrottle: boolean;
    return (...args: Parameters<T>) => {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
};

const OneToOneCropper: React.FC<OneToOneCropperProps> = ({ imageUrl, onCrop, onCancel }) => {
    const imageRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const cropBoxRef = useRef<HTMLDivElement>(null);

    // Motion values for smooth interactions
    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const scale = useMotionValue(1);
    const rotation = useMotionValue(0);

    // State
    const [minScale, setMinScale] = useState(1);
    const [imageDims, setImageDims] = useState<{ width: number; height: number } | null>(null);
    const [dragConstraints, setDragConstraints] = useState({ top: 0, left: 0, right: 0, bottom: 0 });
    const [showGrid, setShowGrid] = useState(true);
    const [showTarget, setShowTarget] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [flipH, setFlipH] = useState(false);
    const [rotateVal, setRotateVal] = useState(0);
    const [hasInitializedFit, setHasInitializedFit] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [scalePercent, setScalePercent] = useState(100);

    // Subscribe to scale changes for display - OPTIMIZED
    useMotionValueEvent(scale, "change", (latest) => {
        setScalePercent(Math.round(latest * 100));
    });

    // PERFORMANCE: Memoize effective dimensions calculation
    const effectiveDims = useMemo(() => {
        if (!imageDims) return null;
        const isRotated = rotateVal % 180 !== 0;
        return {
            width: isRotated ? imageDims.height : imageDims.width,
            height: isRotated ? imageDims.width : imageDims.height
        };
    }, [imageDims, rotateVal]);

    // PERFORMANCE: Memoized constraint calculation
    const calculateConstraints = useCallback(() => {
        if (!effectiveDims || !cropBoxRef.current) return;

        const cropSize = cropBoxRef.current.offsetWidth;
        const currentScale = scale.get();

        // Calculate minimum scale to cover the crop area
        const widthRatio = cropSize / effectiveDims.width;
        const heightRatio = cropSize / effectiveDims.height;
        const newMinScale = Math.max(widthRatio, heightRatio);

        setMinScale(newMinScale);

        // Initial fit: Set scale to cover the area exactly
        if (!hasInitializedFit) {
            scale.set(newMinScale);
            setHasInitializedFit(true);
        }
        // If current scale is too small, animate to new min
        else if (currentScale < newMinScale) {
            animate(scale, newMinScale, { type: 'spring', stiffness: 300, damping: 30 });
        }

        // Calculate drag constraints
        const scaledWidth = effectiveDims.width * Math.max(currentScale, newMinScale);
        const scaledHeight = effectiveDims.height * Math.max(currentScale, newMinScale);

        const xConstraint = Math.max(0, (scaledWidth - cropSize) / 2);
        const yConstraint = Math.max(0, (scaledHeight - cropSize) / 2);

        setDragConstraints({
            left: -xConstraint,
            right: xConstraint,
            top: -yConstraint,
            bottom: yConstraint
        });

        // Clamp current position
        const currentX = x.get();
        const currentY = y.get();

        if (currentX < -xConstraint) x.set(-xConstraint);
        if (currentX > xConstraint) x.set(xConstraint);
        if (currentY < -yConstraint) y.set(-yConstraint);
        if (currentY > yConstraint) y.set(yConstraint);

    }, [effectiveDims, scale, x, y, hasInitializedFit]);

    // PERFORMANCE: Memoized image load handler
    const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        setImageDims({ width: img.naturalWidth, height: img.naturalHeight });
        setRotateVal(0);
        setFlipH(false);
        x.set(0);
        y.set(0);
        scale.set(1);
        setHasInitializedFit(false);
        setImageError(false);
    }, [x, y, scale]);

    // PERFORMANCE: Memoized image error handler
    const handleImageError = useCallback(() => {
        setImageError(true);
        toast.error('Failed to load image. Please try again.');
    }, []);

    // Recalculate when dependencies change
    useEffect(() => {
        calculateConstraints();
    }, [calculateConstraints]);

    // Listen to scale changes
    useEffect(() => scale.on('change', calculateConstraints), [scale, calculateConstraints]);

    // PERFORMANCE: Debounced resize observer
    useEffect(() => {
        if (!cropBoxRef.current) return;

        const debouncedConstraints = debounce(calculateConstraints, 100);
        const observer = new ResizeObserver(debouncedConstraints);
        observer.observe(cropBoxRef.current);

        return () => observer.disconnect();
    }, [calculateConstraints]);

    // PERFORMANCE: Memoized keyboard handler
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (isProcessing) return;

        const step = e.shiftKey ? 10 : 1;
        const currentX = x.get();
        const currentY = y.get();

        switch (e.key) {
            case 'ArrowLeft':
                e.preventDefault();
                x.set(Math.max(dragConstraints.left, currentX - step));
                break;
            case 'ArrowRight':
                e.preventDefault();
                x.set(Math.min(dragConstraints.right, currentX + step));
                break;
            case 'ArrowUp':
                e.preventDefault();
                y.set(Math.max(dragConstraints.top, currentY - step));
                break;
            case 'ArrowDown':
                e.preventDefault();
                y.set(Math.min(dragConstraints.bottom, currentY + step));
                break;
            case 'Escape':
                e.preventDefault();
                onCancel();
                break;
            case 'Enter':
                e.preventDefault();
                if (!isProcessing) handleConfirmCrop();
                break;
        }
    }, [dragConstraints, isProcessing, x, y, onCancel]);

    // Keyboard Navigation
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // PERFORMANCE: Memoized zoom handler with animation
    const handleZoom = useCallback((factor: number) => {
        const newScale = scale.get() * factor;
        animate(scale, Math.max(minScale, Math.min(newScale, 5)), {
            type: 'spring',
            stiffness: 400,
            damping: 30
        });
    }, [scale, minScale]);

    // PERFORMANCE: Throttled wheel handler
    const handleWheel = useCallback(
        throttle((e: React.WheelEvent) => {
            e.preventDefault();
            const factor = e.deltaY > 0 ? 0.95 : 1.05;
            handleZoom(factor);
        }, 16), // 60fps
        [handleZoom]
    );

    // PERFORMANCE: Memoized rotate handler
    const rotateImage = useCallback(() => {
        const nextRotation = (rotateVal + 90) % 360;
        setRotateVal(nextRotation);
        animate(rotation, nextRotation, { type: 'spring', stiffness: 200, damping: 20 });
        x.set(0);
        y.set(0);
    }, [rotateVal, rotation, x, y]);

    // PERFORMANCE: Memoized flip handler
    const toggleFlipH = useCallback(() => {
        setFlipH(prev => !prev);
    }, []);

    // PERFORMANCE: Memoized grid toggle
    const toggleGrid = useCallback(() => {
        setShowGrid(prev => !prev);
    }, []);

    // PERFORMANCE: Memoized target toggle
    const toggleTarget = useCallback(() => {
        setShowTarget(prev => !prev);
    }, []);

    // PERFORMANCE: Memoized crop confirmation
    const handleConfirmCrop = useCallback(async () => {
        if (!imageRef.current || !cropBoxRef.current || isProcessing || !imageDims) return;

        setIsProcessing(true);

        try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d', { alpha: false });

            if (!ctx) throw new Error('Could not get canvas context');

            const outputSize = 2048;
            canvas.width = outputSize;
            canvas.height = outputSize;

            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, outputSize, outputSize);

            const currentScale = scale.get();
            const currentX = x.get();
            const currentY = y.get();
            const cropSize = cropBoxRef.current.offsetWidth;

            ctx.translate(outputSize / 2, outputSize / 2);
            ctx.rotate((rotateVal * Math.PI) / 180);
            if (flipH) ctx.scale(-1, 1);

            const pixelRatio = outputSize / cropSize;

            ctx.scale(currentScale * pixelRatio, currentScale * pixelRatio);
            ctx.translate(currentX / currentScale, currentY / currentScale);

            ctx.drawImage(
                imageRef.current,
                -imageDims.width / 2,
                -imageDims.height / 2
            );

            canvas.toBlob((blob) => {
                if (blob) {
                    const file = new File([blob], `cropped_${Date.now()}.jpg`, { type: 'image/jpeg' });
                    onCrop(file);
                    toast.success('Image cropped successfully');
                } else {
                    throw new Error('Blob creation failed');
                }
                setIsProcessing(false);
            }, 'image/jpeg', 0.95);

        } catch (error) {
            console.error('Crop error:', error);
            toast.error('Failed to crop image. Please try again.');
            setIsProcessing(false);
        }
    }, [imageRef, cropBoxRef, isProcessing, imageDims, scale, x, y, rotateVal, flipH, onCrop]);

    // PERFORMANCE: Memoized slider value
    const sliderValue = useTransform(scale, [minScale, 5], [0, 100]);

    // PERFORMANCE: Memoized spring config
    const springConfig = useMemo(() => ({
        type: 'spring' as const,
        stiffness: 400,
        damping: 20
    }), []);

    // MOBILE: Pinch-to-zoom support
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let initialDistance = 0;
        let initialScale = 1;

        const handleTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                const touch1 = e.touches[0];
                const touch2 = e.touches[1];
                initialDistance = Math.hypot(
                    touch2.clientX - touch1.clientX,
                    touch2.clientY - touch1.clientY
                );
                initialScale = scale.get();
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (e.touches.length === 2) {
                e.preventDefault();
                const touch1 = e.touches[0];
                const touch2 = e.touches[1];
                const currentDistance = Math.hypot(
                    touch2.clientX - touch1.clientX,
                    touch2.clientY - touch1.clientY
                );
                const scaleFactor = currentDistance / initialDistance;
                const newScale = initialScale * scaleFactor;
                scale.set(Math.max(minScale, Math.min(newScale, 5)));
            }
        };

        container.addEventListener('touchstart', handleTouchStart, { passive: false });
        container.addEventListener('touchmove', handleTouchMove, { passive: false });

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
        };
    }, [scale, minScale]);

    if (imageError) {
        return (
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-zinc-950 text-white">
                <div className="text-center p-8">
                    <p className="text-lg text-red-400 mb-4">Failed to load image</p>
                    <button
                        onClick={onCancel}
                        className="px-6 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 transition-colors"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex flex-col md:flex-row bg-gradient-to-br from-[#09090b] via-[#0a0a0c] to-[#09090b] text-white overflow-hidden font-sans selection:bg-teal-500/30"
            style={{ WebkitTapHighlightColor: 'transparent' }}
        >
            {/* Ambient Background Effects */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {/* Gradient Mesh - OPTIMIZED with will-change */}
                <motion.div
                    className="absolute inset-0"
                    style={{ willChange: 'background' }}
                    animate={{
                        background: [
                            'radial-gradient(circle at 20% 20%, rgba(20, 184, 166, 0.08) 0%, transparent 50%)',
                            'radial-gradient(circle at 80% 80%, rgba(20, 184, 166, 0.08) 0%, transparent 50%)',
                            'radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.08) 0%, transparent 50%)',
                            'radial-gradient(circle at 20% 20%, rgba(20, 184, 166, 0.08) 0%, transparent 50%)',
                        ]
                    }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                />
                {/* Subtle Grid Pattern */}
                <div className="absolute inset-0 opacity-[0.015]"
                    style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}
                />
            </div>

            {/* Main Crop Area */}
            <div className="relative flex-1 flex flex-col h-[55vh] md:h-full overflow-hidden">

                {/* Mobile Header - OPTIMIZED */}
                <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.1, ...springConfig }}
                    className="absolute top-0 left-0 right-0 p-3 md:p-4 z-30 flex justify-between items-center md:hidden"
                    style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
                >
                    <div className="glass backdrop-blur-2xl bg-zinc-900/60 border border-white/10 rounded-2xl px-3 py-2 flex items-center gap-2 shadow-2xl">
                        <button
                            onClick={onCancel}
                            aria-label="Cancel"
                            className="p-2 -ml-1 rounded-xl hover:bg-white/10 text-white/80 hover:text-white active:scale-95 transition-all touch-manipulation"
                        >
                            <X size={20} strokeWidth={2.5} />
                        </button>
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded-lg bg-teal-500/10 border border-teal-500/20">
                                <Layers size={14} className="text-teal-400" />
                            </div>
                            <span className="font-semibold text-sm text-white tracking-tight">Crop & Adjust</span>
                        </div>
                    </div>
                    <button
                        onClick={handleConfirmCrop}
                        disabled={isProcessing}
                        aria-label="Confirm Crop"
                        className="glass backdrop-blur-2xl bg-gradient-to-br from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 border border-teal-400/20 rounded-2xl px-4 py-2.5 shadow-2xl shadow-teal-500/25 active:scale-95 transition-all disabled:opacity-50 flex items-center gap-2 touch-manipulation"
                    >
                        {isProcessing ? (
                            <RefreshCw className="animate-spin" size={18} strokeWidth={2.5} />
                        ) : (
                            <Check size={18} strokeWidth={2.5} />
                        )}
                        <span className="font-bold text-sm">Done</span>
                    </button>
                </motion.div>

                {/* Canvas / Interaction Area - OPTIMIZED */}
                <div
                    ref={containerRef}
                    className="flex-1 flex items-center justify-center relative overflow-hidden cursor-grab active:cursor-grabbing touch-none"
                    onWheel={handleWheel}
                    style={{ touchAction: 'none' }}
                >
                    <motion.div
                        drag
                        dragConstraints={dragConstraints}
                        dragElastic={0.05}
                        dragMomentum={false}
                        style={{
                            x,
                            y,
                            scale,
                            rotate: rotation,
                            willChange: 'transform'
                        }}
                        className="relative z-10"
                        whileDrag={{ cursor: 'grabbing' }}
                    >
                        <motion.img
                            ref={imageRef}
                            src={imageUrl}
                            onLoad={handleImageLoad}
                            onError={handleImageError}
                            alt="Crop preview"
                            className="max-w-none pointer-events-none select-none shadow-2xl"
                            style={{
                                scaleX: flipH ? -1 : 1,
                                willChange: 'transform'
                            }}
                            draggable={false}
                        />
                    </motion.div>

                    {/* Premium Crop Frame Overlay - OPTIMIZED */}
                    <div className="absolute inset-0 pointer-events-none z-20">
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div
                                ref={cropBoxRef}
                                className="w-[min(85vw,85vh)] h-[min(85vw,85vh)] sm:w-[min(75vw,75vh)] sm:h-[min(75vw,75vh)] md:w-[min(65vw,65vh)] md:h-[min(65vw,65vh)] relative group"
                                style={{
                                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.75)',
                                }}
                            >
                                {/* Main Border with Gradient Glow - OPTIMIZED */}
                                <motion.div
                                    className="absolute inset-0 rounded-[24px] sm:rounded-[32px] overflow-hidden"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 0.2, ...springConfig }}
                                >
                                    {/* Static Border */}
                                    <div className="absolute inset-0 rounded-[24px] sm:rounded-[32px] border-2 border-white/20" />
                                    {/* Animated Border Glow - OPTIMIZED */}
                                    <motion.div
                                        className="absolute inset-0 rounded-[24px] sm:rounded-[32px]"
                                        style={{
                                            background: 'linear-gradient(45deg, transparent 40%, rgba(45, 212, 191, 0.3) 50%, transparent 60%)',
                                            backgroundSize: '200% 200%',
                                            willChange: 'background-position'
                                        }}
                                        animate={{
                                            backgroundPosition: ['0% 0%', '100% 100%', '0% 0%'],
                                        }}
                                        transition={{
                                            duration: 4,
                                            repeat: Infinity,
                                            ease: 'linear'
                                        }}
                                    />
                                </motion.div>

                                {/* Enhanced Corner Markers - OPTIMIZED */}
                                {[
                                    { top: 0, left: 0, name: 'tl' },
                                    { top: 0, right: 0, name: 'tr' },
                                    { bottom: 0, left: 0, name: 'bl' },
                                    { bottom: 0, right: 0, name: 'br' },
                                ].map((corner, idx) => (
                                    <motion.div
                                        key={corner.name}
                                        className="absolute w-12 sm:w-16 h-12 sm:h-16 z-30"
                                        style={corner}
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.3 + idx * 0.05, ...springConfig }}
                                    >
                                        {/* Horizontal Line */}
                                        <div
                                            className={`absolute ${corner.top !== undefined ? 'top-0' : 'bottom-0'} ${corner.left !== undefined ? 'left-0' : 'right-0'} w-full h-0.5`}
                                            style={{
                                                background: corner.left !== undefined
                                                    ? 'linear-gradient(to right, rgba(45, 212, 191, 1), rgba(45, 212, 191, 0))'
                                                    : 'linear-gradient(to left, rgba(45, 212, 191, 1), rgba(45, 212, 191, 0))',
                                                boxShadow: '0 0 12px rgba(45, 212, 191, 0.6)',
                                            }}
                                        />
                                        {/* Vertical Line */}
                                        <div
                                            className={`absolute ${corner.top !== undefined ? 'top-0' : 'bottom-0'} ${corner.left !== undefined ? 'left-0' : 'right-0'} h-full w-0.5`}
                                            style={{
                                                background: corner.top !== undefined
                                                    ? 'linear-gradient(to bottom, rgba(45, 212, 191, 1), rgba(45, 212, 191, 0))'
                                                    : 'linear-gradient(to top, rgba(45, 212, 191, 1), rgba(45, 212, 191, 0))',
                                                boxShadow: '0 0 12px rgba(45, 212, 191, 0.6)',
                                            }}
                                        />
                                        {/* Corner Dot */}
                                        <div
                                            className={`absolute ${corner.top !== undefined ? 'top-0' : 'bottom-0'} ${corner.left !== undefined ? 'left-0' : 'right-0'} w-2 h-2 bg-teal-400 rounded-full`}
                                            style={{
                                                boxShadow: '0 0 16px rgba(45, 212, 191, 0.8)',
                                            }}
                                        />
                                    </motion.div>
                                ))}

                                {/* Grid Lines - Elegant */}
                                <AnimatePresence>
                                    {showGrid && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                            className="absolute inset-0 pointer-events-none z-10"
                                        >
                                            <div className="absolute left-1/3 top-4 sm:top-6 bottom-4 sm:bottom-6 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent"></div>
                                            <div className="absolute right-1/3 top-4 sm:top-6 bottom-4 sm:bottom-6 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent"></div>
                                            <div className="absolute top-1/3 left-4 sm:left-6 right-4 sm:right-6 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"></div>
                                            <div className="absolute bottom-1/3 left-4 sm:left-6 right-4 sm:right-6 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"></div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Center Target - Refined */}
                                <AnimatePresence>
                                    {showTarget && (
                                        <motion.div
                                            initial={{ opacity: 0, scale: 0.5 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.5 }}
                                            transition={springConfig}
                                            className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
                                        >
                                            <div className="relative">
                                                {/* Center Dot */}
                                                <div className="w-2 h-2 bg-white rounded-full shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                                                {/* Outer Ring */}
                                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 sm:w-10 h-8 sm:h-10 border border-teal-500/20 rounded-full" />
                                                {/* Crosshair Lines */}
                                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-px h-12 sm:h-16 bg-gradient-to-b from-transparent via-teal-500/30 to-transparent" />
                                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-px w-12 sm:w-16 bg-gradient-to-r from-transparent via-teal-500/30 to-transparent" />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Desktop Floating Toolbar - OPTIMIZED */}
                <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.4, ...springConfig }}
                    className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-30 hidden md:flex items-center gap-1 p-1.5 rounded-2xl glass backdrop-blur-2xl bg-zinc-900/70 border border-white/10 shadow-2xl"
                >
                    <button
                        onClick={toggleGrid}
                        aria-label="Toggle Grid"
                        className={`p-3.5 rounded-xl transition-all duration-200 group touch-manipulation ${showGrid ? 'bg-teal-500/15 text-teal-400 shadow-lg shadow-teal-500/10' : 'hover:bg-white/5 text-zinc-400 hover:text-white'}`}
                        title="Toggle Grid (Rule of Thirds)"
                    >
                        <Grid3X3 size={19} strokeWidth={2} className="group-hover:scale-110 transition-transform" style={{ willChange: 'transform' }} />
                    </button>
                    <button
                        onClick={toggleTarget}
                        aria-label="Toggle Center"
                        className={`p-3.5 rounded-xl transition-all duration-200 group touch-manipulation ${showTarget ? 'bg-teal-500/15 text-teal-400 shadow-lg shadow-teal-500/10' : 'hover:bg-white/5 text-zinc-400 hover:text-white'}`}
                        title="Toggle Center Crosshair"
                    >
                        <Crosshair size={19} strokeWidth={2} className="group-hover:scale-110 transition-transform" style={{ willChange: 'transform' }} />
                    </button>
                    <div className="w-px h-7 bg-white/10 mx-1.5" />
                    <button
                        onClick={() => handleZoom(0.9)}
                        aria-label="Zoom Out"
                        className="p-3.5 rounded-xl hover:bg-white/5 text-zinc-400 hover:text-white transition-all group touch-manipulation"
                        title="Zoom Out"
                    >
                        <ZoomOut size={19} strokeWidth={2} className="group-hover:scale-110 transition-transform" style={{ willChange: 'transform' }} />
                    </button>
                    <button
                        onClick={() => handleZoom(1.1)}
                        aria-label="Zoom In"
                        className="p-3.5 rounded-xl hover:bg-white/5 text-zinc-400 hover:text-white transition-all group touch-manipulation"
                        title="Zoom In"
                    >
                        <ZoomIn size={19} strokeWidth={2} className="group-hover:scale-110 transition-transform" style={{ willChange: 'transform' }} />
                    </button>
                </motion.div>
            </div>

            {/* Sidebar / Control Panel - OPTIMIZED */}
            <motion.div
                initial={{ x: 20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2, ...springConfig }}
                className="w-full md:w-[380px] lg:w-[400px] glass backdrop-blur-2xl bg-zinc-900/40 border-t md:border-t-0 md:border-l border-white/5 flex flex-col shadow-2xl z-30 relative"
                style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
                {/* Desktop Header */}
                <div className="p-4 sm:p-6 border-b border-white/5 hidden md:block bg-zinc-900/30">
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-3">
                                <div className="p-2.5 rounded-xl bg-gradient-to-br from-teal-500/20 to-teal-600/10 border border-teal-500/20 shadow-lg shadow-teal-500/10">
                                    <Square size={20} className="text-teal-400" strokeWidth={2.5} />
                                </div>
                                Crop & Adjust
                            </h2>
                            <p className="text-xs text-zinc-400 mt-2 font-medium">Perfect your image composition</p>
                        </div>
                        <button
                            onClick={onCancel}
                            className="p-2.5 rounded-xl hover:bg-white/5 text-zinc-400 hover:text-white transition-all active:scale-95 touch-manipulation"
                            aria-label="Close"
                        >
                            <X size={20} strokeWidth={2.5} />
                        </button>
                    </div>

                    {/* Zoom Display Badge - OPTIMIZED */}
                    <motion.div
                        className="glass backdrop-blur-xl bg-gradient-to-br from-teal-500/10 to-transparent border border-teal-500/20 rounded-xl p-4 flex items-center justify-between"
                        whileHover={{ scale: 1.02 }}
                        transition={springConfig}
                    >
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-teal-500/20 border border-teal-500/30">
                                <Maximize2 size={16} className="text-teal-400" strokeWidth={2.5} />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase tracking-wider text-teal-400/70 font-bold">Zoom Level</p>
                                <p className="text-2xl font-bold text-teal-400 tracking-tight tabular-nums">
                                    {scalePercent}%
                                </p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Output</p>
                            <p className="text-xs font-mono text-zinc-400 tabular-nums">2048×2048</p>
                        </div>
                    </motion.div>
                </div>

                {/* Scrollable Content - OPTIMIZED */}
                <div className="flex-1 overflow-y-auto custom-scrollbar overscroll-contain">
                    <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">

                        {/* Zoom Slider - OPTIMIZED */}
                        <div className="space-y-4">
                            <label className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                                <div className="w-1 h-4 bg-teal-500 rounded-full" />
                                Zoom Control
                            </label>
                            <div className="relative h-12 flex items-center group">
                                {/* Track Background */}
                                <div className="absolute w-full h-2 bg-zinc-800/50 rounded-full overflow-hidden border border-white/5">
                                    <motion.div
                                        className="h-full bg-gradient-to-r from-teal-600 via-teal-500 to-teal-400 shadow-lg shadow-teal-500/30"
                                        style={{
                                            width: useTransform(sliderValue, (v) => `${v}%`),
                                            willChange: 'width'
                                        }}
                                    />
                                </div>
                                {/* Input */}
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="1"
                                    defaultValue="0"
                                    onChange={(e) => {
                                        const val = parseInt(e.target.value);
                                        const newScale = minScale + (val / 100) * (5 - minScale);
                                        animate(scale, newScale, springConfig);
                                    }}
                                    className="absolute w-full h-full opacity-0 cursor-pointer z-10 touch-manipulation"
                                    aria-label="Zoom level"
                                />
                                {/* Thumb - OPTIMIZED */}
                                <motion.div
                                    className="absolute w-5 h-5 bg-white rounded-full shadow-2xl border-2 border-teal-500 pointer-events-none z-20"
                                    style={{
                                        left: useTransform(sliderValue, (v) => `calc(${v}% - 10px)`),
                                        willChange: 'left'
                                    }}
                                    whileHover={{ scale: 1.3 }}
                                >
                                    <div className="absolute inset-0 rounded-full bg-teal-400/30 blur-md" />
                                </motion.div>
                            </div>
                        </div>

                        {/* Transform Controls - OPTIMIZED */}
                        <div className="space-y-4">
                            <label className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                                <div className="w-1 h-4 bg-teal-500 rounded-full" />
                                Transform
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                {/* Rotate Button */}
                                <motion.button
                                    onClick={rotateImage}
                                    className="relative overflow-hidden group p-4 sm:p-5 rounded-2xl glass backdrop-blur-xl bg-zinc-800/40 border border-white/10 hover:border-teal-500/40 transition-all text-left touch-manipulation"
                                    whileHover={{ scale: 1.03, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                    transition={springConfig}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-teal-500/0 to-teal-500/0 group-hover:from-teal-500/5 group-hover:to-teal-500/10 transition-all duration-300" />
                                    <RotateCw size={22} className="text-zinc-400 group-hover:text-teal-400 mb-2 sm:mb-3 transition-colors group-hover:rotate-90 duration-300" strokeWidth={2.5} style={{ willChange: 'transform' }} />
                                    <div className="text-sm font-bold text-zinc-200 group-hover:text-white transition-colors">Rotate</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">90° Clockwise</div>
                                </motion.button>

                                {/* Flip Button */}
                                <motion.button
                                    onClick={toggleFlipH}
                                    className={`relative overflow-hidden group p-4 sm:p-5 rounded-2xl glass backdrop-blur-xl border transition-all text-left touch-manipulation ${flipH
                                        ? 'bg-teal-500/15 border-teal-500/50 shadow-lg shadow-teal-500/20'
                                        : 'bg-zinc-800/40 border-white/10 hover:border-teal-500/40'
                                        }`}
                                    whileHover={{ scale: 1.03, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                    transition={springConfig}
                                >
                                    <div className={`absolute inset-0 bg-gradient-to-br transition-all duration-300 ${flipH
                                        ? 'from-teal-500/10  to-teal-500/5'
                                        : 'from-teal-500/0 to-teal-500/0 group-hover:from-teal-500/5 group-hover:to-teal-500/10'
                                        }`} />
                                    <FlipHorizontal
                                        size={22}
                                        className={`mb-2 sm:mb-3 transition-colors duration-300 ${flipH ? 'text-teal-400' : 'text-zinc-400 group-hover:text-teal-400'
                                            }`}
                                        strokeWidth={2.5}
                                    />
                                    <div className={`text-sm font-bold transition-colors ${flipH ? 'text-teal-100' : 'text-zinc-200 group-hover:text-white'}`}>
                                        Flip H
                                    </div>
                                    <div className={`text-xs mt-0.5 ${flipH ? 'text-teal-400/70' : 'text-zinc-500'}`}>
                                        {flipH ? 'Active' : 'Mirror image'}
                                    </div>
                                </motion.button>
                            </div>
                        </div>

                        {/* Mobile Only Grid Controls - OPTIMIZED */}
                        <div className="md:hidden space-y-4">
                            <label className="text-xs font-bold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                                <div className="w-1 h-4 bg-teal-500 rounded-full" />
                                Visual Guides
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                                <motion.button
                                    onClick={toggleGrid}
                                    className={`p-4 rounded-2xl glass backdrop-blur-xl border flex items-center justify-center gap-2 transition-all touch-manipulation ${showGrid
                                        ? 'bg-teal-500/15 border-teal-500/50 text-teal-400 shadow-lg shadow-teal-500/20'
                                        : 'bg-zinc-800/40 border-white/10 text-zinc-400 hover:text-white hover:border-teal-500/40'
                                        }`}
                                    whileTap={{ scale: 0.95 }}
                                    aria-pressed={showGrid}
                                >
                                    <Grid3X3 size={18} strokeWidth={2.5} />
                                    <span className="text-xs font-bold">Grid</span>
                                </motion.button>
                                <motion.button
                                    onClick={toggleTarget}
                                    className={`p-4 rounded-2xl glass backdrop-blur-xl border flex items-center justify-center gap-2 transition-all touch-manipulation ${showTarget
                                        ? 'bg-teal-500/15 border-teal-500/50 text-teal-400 shadow-lg shadow-teal-500/20'
                                        : 'bg-zinc-800/40 border-white/10 text-zinc-400 hover:text-white hover:border-teal-500/40'
                                        }`}
                                    whileTap={{ scale: 0.95 }}
                                    aria-pressed={showTarget}
                                >
                                    <Crosshair size={18} strokeWidth={2.5} />
                                    <span className="text-xs font-bold">Center</span>
                                </motion.button>
                            </div>
                        </div>

                        {/* Pro Tips Card - OPTIMIZED */}
                        <motion.div
                            className="glass backdrop-blur-xl bg-blue-500/5 border border-blue-500/20 rounded-2xl p-4 sm:p-5 flex gap-3 sm:gap-4 items-start"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <div className="shrink-0 p-2.5 rounded-xl bg-blue-500/10 border border-blue-500/20">
                                <MousePointer2 className="text-blue-400" size={18} strokeWidth={2.5} />
                            </div>
                            <div className="space-y-2">
                                <p className="text-xs font-bold text-blue-300 uppercase tracking-wider">Pro Tips</p>
                                <ul className="text-[11px] text-blue-200/70 leading-relaxed space-y-1.5">
                                    <li className="flex items-start gap-2">
                                        <span className="text-blue-400 shrink-0 mt-0.5">•</span>
                                        <span><strong className="text-blue-300">Pinch</strong> to zoom on mobile</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-blue-400 shrink-0 mt-0.5">•</span>
                                        <span>Use <strong className="text-blue-300">mouse wheel</strong> for precise zoom</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-blue-400 shrink-0 mt-0.5">•</span>
                                        <span><strong className="text-blue-300">Drag</strong> to reposition the image</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-blue-400 shrink-0 mt-0.5">•</span>
                                        <span>Press <strong className="text-blue-300">Enter</strong> to confirm or <strong className="text-blue-300">Esc</strong> to cancel</span>
                                    </li>
                                </ul>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Footer Actions - OPTIMIZED */}
                <div className="p-4 sm:p-6 border-t border-white/5 glass backdrop-blur-2xl bg-zinc-900/60 space-y-3 z-20">
                    <motion.button
                        onClick={handleConfirmCrop}
                        disabled={isProcessing}
                        className="relative w-full py-4 rounded-2xl bg-gradient-to-r from-teal-600 via-teal-500 to-teal-600 hover:from-teal-500 hover:via-teal-400 hover:to-teal-500 text-white font-bold shadow-2xl shadow-teal-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all overflow-hidden group border border-teal-400/20 touch-manipulation"
                        whileHover={{ scale: 1.02, y: -1 }}
                        whileTap={{ scale: 0.98 }}
                        transition={springConfig}
                        aria-busy={isProcessing}
                    >
                        <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" style={{ willChange: 'transform' }} />
                        {isProcessing ? (
                            <>
                                <RefreshCw className="animate-spin" size={20} strokeWidth={2.5} />
                                <span className="text-base">Processing...</span>
                            </>
                        ) : (
                            <>
                                <Check size={20} strokeWidth={3} />
                                <span className="text-base">Confirm Crop</span>
                            </>
                        )}
                    </motion.button>
                    <motion.button
                        onClick={onCancel}
                        className="w-full py-3.5 rounded-2xl glass backdrop-blur-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 text-zinc-300 hover:text-white font-semibold transition-all text-sm touch-manipulation"
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                    >
                        Cancel
                    </motion.button>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default OneToOneCropper;
